import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const KEY = "z6_table";
const TTL = 4 * 3600 * 1000;
// Table visit lasts ~4h; after that (or Start over) the next guests start clean.
const readSid = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    if (v && Date.now() - v.at < TTL) return v.id;
  } catch { /* legacy plain id */ }
  localStorage.removeItem(KEY);
  return null;
};
const saveSid = (id) => localStorage.setItem(KEY, JSON.stringify({ id, at: Date.now() }));
const TableContext = createContext(null);
export const simulateLlmFailure = () => localStorage.getItem("z6_sim_llm_fail") === "1";

export function TableProvider({ children }) {
  const [table, setTable] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const sid = readSid();
    if (!sid) return setReady(true);
    api.get(`/table/${sid}`).then((r) => setTable(r.data)).catch(() => localStorage.removeItem(KEY)).finally(() => setReady(true));
  }, []);

  const run = useCallback(async (p) => {
    const r = await p;
    const t = r.data.table || r.data;
    setTable(t);
    return r.data;
  }, []);

  const create = useCallback(async (guest_count) => {
    const r = await api.post("/table", { guest_count });
    saveSid(r.data.id);
    setTable(r.data);
    return r.data;
  }, []);

  const ensure = useCallback(async () => table || create(1), [table, create]);
  const sid = table?.id;
  const g = (gid) => `/table/${sid}/guests/${gid}`;

  const actions = useMemo(() => ({
    setActive: (gid) => run(api.patch(`/table/${sid}`, { active_guest: gid })),
    setTableNumber: (n) => run(api.patch(`/table/${sid}`, { table_number: n })),
    addGuest: (name) => run(api.post(`/table/${sid}/guests`, { name })),
    removeGuest: (gid) => run(api.delete(g(gid))),
    renameGuest: (gid, name) => run(api.patch(g(gid), { name })),
    submitAnswers: (gid, answers) => run(api.post(`${g(gid)}/answers`, { answers, simulate_llm_failure: simulateLlmFailure() })),
    followup: (gid, body) => run(api.post(`${g(gid)}/followup`, { ...body, simulate_llm_failure: simulateLlmFailure() })),
    setDrink: (gid, dish_id, serve) => run(api.put(`${g(gid)}/drink`, { dish_id, serve: serve || null })),
    dropDrink: (gid) => run(api.delete(`${g(gid)}/drink`)),
    drinks: async (gid) => (await api.get(`${g(gid)}/drinks`)).data.drinks,
    parse: async (gid, step, text) => (await api.post(`${g(gid)}/parse`, { step, text, simulate_llm_failure: simulateLlmFailure() })).data,
    reset: async () => {
      if (sid) await api.delete(`/table/${sid}`).catch(() => {});
      localStorage.removeItem(KEY);
      window.dispatchEvent(new Event("z6-reset"));
      setTable(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sid, run]);

  const setPlate = useCallback(async (course, dish_id, opts = {}) => {
    const t = await ensure();
    const gid = opts.guestId || t.active_guest;
    const r = await api.put(`/table/${t.id}/guests/${gid}/plate`, { course, dish_id, serve: opts.serve || null, warn_allergens: opts.warn || [], force: !!opts.force, simulate_llm_failure: simulateLlmFailure() });
    setTable(r.data.table);
    return r.data;
  }, [ensure]);

  const active = table?.guests.find((x) => x.id === table.active_guest) || table?.guests[0] || null;
  const value = useMemo(() => ({ table, ready, active, create, ensure, setPlate, ...actions }), [table, ready, active, create, ensure, setPlate, actions]);
  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}

export const useTable = () => useContext(TableContext);
